import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import pacientesService, { CreateConsultaDto, Paciente } from '../../services/pacientesService';
import { 
  ArrowLeft as ArrowLeftIcon, 
  Calendar as CalendarIcon, 
  Plus as PlusIcon, 
  Trash2 as TrashIcon,
  FileText as DocumentTextIcon
} from 'lucide-react';

export default function ConsultaPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const pacienteId = Number(id);

  const [loading, setLoading] = useState(false);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  
  const [formData, setFormData] = useState<CreateConsultaDto>({
    pacienteId,
    fecha: new Date().toISOString().slice(0, 16),
    temperatura: undefined,
    peso: undefined,
    estatura: undefined,
    revisionFisica: '',
    observaciones: '',
    diagnosticos: [],
    medicamentos: [],
  });

  useEffect(() => {
    loadPaciente();
  }, [pacienteId]);

  const loadPaciente = async () => {
    try {
      const data = await pacientesService.getPaciente(pacienteId);
      setPaciente(data);
      // Pre-cargar peso y estatura del paciente
      setFormData(prev => ({
        ...prev,
        peso: data.peso || undefined,
        estatura: data.estatura || undefined,
      }));
    } catch (error) {
      console.error('Error al cargar paciente:', error);
      toast.error('Error al cargar paciente');
      navigate('/pacientes');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.diagnosticos || formData.diagnosticos.length === 0) {
      toast.error('Debe agregar al menos un diagnóstico');
      return;
    }

    try {
      setLoading(true);
      const consulta = await pacientesService.createConsulta(formData);
      toast.success('Consulta registrada exitosamente');
      
      // Preguntar si desea imprimir la receta
      if (confirm('¿Desea imprimir la receta?')) {
        navigate(`/pacientes/consultas/${consulta.id}/receta`);
      } else {
        navigate('/pacientes');
      }
    } catch (error) {
      console.error('Error al guardar consulta:', error);
      toast.error('Error al guardar consulta');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? undefined : value,
    }));
  };

  const addDiagnostico = () => {
    setFormData(prev => ({
      ...prev,
      diagnosticos: [...(prev.diagnosticos || []), { diagnostico: '' }],
    }));
  };

  const removeDiagnostico = (index: number) => {
    setFormData(prev => ({
      ...prev,
      diagnosticos: prev.diagnosticos?.filter((_, i) => i !== index),
    }));
  };

  const updateDiagnostico = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      diagnosticos: prev.diagnosticos?.map((d, i) => 
        i === index ? { diagnostico: value } : d
      ),
    }));
  };

  const addMedicamento = () => {
    setFormData(prev => ({
      ...prev,
      medicamentos: [...(prev.medicamentos || []), {
        medicamento: '',
        dosis: '',
        frecuencia: '',
        duracion: '',
        indicaciones: '',
      }],
    }));
  };

  const removeMedicamento = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medicamentos: prev.medicamentos?.filter((_, i) => i !== index),
    }));
  };

  const updateMedicamento = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      medicamentos: prev.medicamentos?.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      ),
    }));
  };

  const calcularEdad = (fechaNacimiento: string) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    if (edad < 1) {
      const meses = mes < 0 ? 12 + mes : mes;
      return `${meses} meses`;
    }
    
    return `${edad} años`;
  };

  if (!paciente) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pacientes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-8 h-8 text-blue-600" />
              Nueva Consulta
            </h1>
            <p className="text-gray-600 mt-1">
              Paciente: <span className="font-semibold">{paciente.nombre}</span> - {calcularEdad(paciente.fechaNacimiento)}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/pacientes/${pacienteId}/historial`)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <DocumentTextIcon className="w-5 h-5" />
          Ver Historial Completo
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos Vitales */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos Vitales</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha y Hora
              </label>
              <input
                type="datetime-local"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temperatura (°C)
              </label>
              <input
                type="number"
                step="0.1"
                name="temperatura"
                value={formData.temperatura || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="36.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Peso (kg)
              </label>
              <input
                type="number"
                step="0.01"
                name="peso"
                value={formData.peso || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="15.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estatura (cm)
              </label>
              <input
                type="number"
                step="0.01"
                name="estatura"
                value={formData.estatura || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="105.5"
              />
            </div>
          </div>
        </div>

        {/* Revisión Física */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revisión Física</h2>
          <textarea
            name="revisionFisica"
            value={formData.revisionFisica}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Descripción detallada de la revisión física..."
          />
        </div>

        {/* Diagnósticos */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Diagnósticos <span className="text-red-500">*</span>
            </h2>
            <button
              type="button"
              onClick={addDiagnostico}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Agregar
            </button>
          </div>
          <div className="space-y-3">
            {formData.diagnosticos?.map((diag, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={diag.diagnostico}
                  onChange={(e) => updateDiagnostico(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Diagnóstico..."
                  required
                />
                <button
                  type="button"
                  onClick={() => removeDiagnostico(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
            {(!formData.diagnosticos || formData.diagnosticos.length === 0) && (
              <p className="text-gray-500 text-sm">No hay diagnósticos agregados</p>
            )}
          </div>
        </div>

        {/* Medicamentos */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Medicamentos Recetados</h2>
            <button
              type="button"
              onClick={addMedicamento}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Agregar
            </button>
          </div>
          <div className="space-y-4">
            {formData.medicamentos?.map((med, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Medicamento {index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => removeMedicamento(index)}
                    className="text-red-600 hover:bg-red-50 p-1 rounded transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={med.medicamento}
                    onChange={(e) => updateMedicamento(index, 'medicamento', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Nombre del medicamento"
                    required
                  />
                  <input
                    type="text"
                    value={med.dosis}
                    onChange={(e) => updateMedicamento(index, 'dosis', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Dosis (ej: 5ml)"
                    required
                  />
                  <input
                    type="text"
                    value={med.frecuencia}
                    onChange={(e) => updateMedicamento(index, 'frecuencia', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Frecuencia (ej: Cada 8 horas)"
                    required
                  />
                  <input
                    type="text"
                    value={med.duracion}
                    onChange={(e) => updateMedicamento(index, 'duracion', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Duración (ej: 7 días)"
                    required
                  />
                  <textarea
                    value={med.indicaciones}
                    onChange={(e) => updateMedicamento(index, 'indicaciones', e.target.value)}
                    className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Indicaciones adicionales..."
                    rows={2}
                  />
                </div>
              </div>
            ))}
            {(!formData.medicamentos || formData.medicamentos.length === 0) && (
              <p className="text-gray-500 text-sm">No hay medicamentos agregados</p>
            )}
          </div>
        </div>

        {/* Observaciones */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Observaciones Generales</h2>
          <textarea
            name="observaciones"
            value={formData.observaciones}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Observaciones adicionales, recomendaciones, etc..."
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/pacientes')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Guardando...' : 'Guardar Consulta'}
          </button>
        </div>
      </form>
    </div>
  );
}
